import { create } from 'zustand';
import { useMemo } from 'react';
import {
  DEFAULT_ITEMS_PER_PAGE,
  ITEMS_PER_PAGE_LIST,
  LIMIT_PARAM_NAME,
  PAGE_PARAM_NAME,
  START_PAGE,
} from 'shared/ui/Pagination/constants';
import { useUrlResolver } from 'shared/lib/url';
import { getKeys } from 'shared/lib/object';
import { useLocation } from 'react-router-dom';
import { dateToString, getDefaultEndDate, getDefaultStartDate, stringToDate } from 'shared/lib/format';
import { buildArrayQueryString } from 'shared/lib/functions';
import { QUERY_MAX_LENGTH, SEARCH_PARAM_NAME } from 'shared/constants';

export type CommonFilterShape = {
  filters: Record<string, string>;
  getFilter: (search: string) => Record<string, string>;
};

interface FetchControllerParamsUrl {
  queryId?: string;
  urlFilters: CommonFilterShape[] | null;
}

type UrlStore = {
  urlFilter: string;
  actions: {
    setFilters: (urlFilter: string) => void;
    getFilter: () => string;
  };
};

const useUrlStore = create<UrlStore>((set, get) => ({
  urlFilter: '',
  actions: {
    setFilters: (urlFilter) => set({ urlFilter }),
    getFilter: () => get().urlFilter,
  },
}));

const useUpdateUrlFilter = () => useUrlStore((state) => state.actions);

export const useGetFiltersUrl = ({ urlFilters }: FetchControllerParamsUrl) => {
  const location = useLocation();
  const { setFilters, getFilter } = useUpdateUrlFilter();

  const handleFilers = (search: string) => {
    let queryParams = '';

    // all of this is unnecessary complexity will be removed right after useFetchController will be deleted
    urlFilters &&
      urlFilters.forEach((filterObj) => {
        Object.values(filterObj.getFilter(search)).forEach((strVal) => {
          queryParams += `&${strVal}`;
        });
      });
    const queryParamsString = queryParams ? `?${new URLSearchParams(queryParams).toString()}` : '';

    if (queryParamsString.length < QUERY_MAX_LENGTH) {
      setFilters(queryParamsString);
    }

    return getFilter();
  };

  const filters = useMemo(() => handleFilers(location.search), [location]);

  return { filtersFromURL: filters };
};

export function getPaginationParamsFromUrl(search: string) {
  const searchParams = new URLSearchParams(search);
  const page = searchParams.get(PAGE_PARAM_NAME);
  const limit = searchParams.get(LIMIT_PARAM_NAME);

  return {
    page,
    limit,
    hasParams: !!(page ?? limit),
  };
}

export function getCustomParamFromUrl(paramName: string, searchString: string) {
  const searchParams = new URLSearchParams(searchString);

  const params = searchParams.getAll(paramName);

  return {
    params,
    hasParams: !!params.length,
  };
}

export function getFromToFiltersFromUrl(paramNameStartsWith: string, searchString: string) {
  const searchParams = new URLSearchParams(searchString);
  let filters: Record<string, string> = {};

  searchParams.forEach((value, key) => {
    if (key.startsWith(paramNameStartsWith)) {
      filters[key] = `${key}=${value}`;
    }
  });

  return filters;
}

export function getDateParamsFromUrl(paramFromName: string, paramToName: string, searchString: string) {
  const searchParams = new URLSearchParams(searchString);
  const from = searchParams.get(paramFromName);
  const to = searchParams.get(paramToName);

  return {
    from: from ? stringToDate(from) : null,
    to: to ? stringToDate(to) : null,
  };
}

// it will be simplified after removal of a store
export function useGetInitialSortFromUrl(sortingKeys: Record<string, string>): CommonFilterShape {
  let filters: Record<string, string> = {};

  const getFilter = (search?: string) => {
    for (let key of getKeys(sortingKeys)) {
      const sortingName = sortingKeys[key];
      const { params, hasParams } = getCustomParamFromUrl(sortingName, search ?? window.location.search);

      if (hasParams) {
        filters[sortingName] = params[0];
      }
    }

    return filters;
  };

  getFilter();

  return { filters, getFilter };
}

export function useGetSortFromUrl(
  sortingKeys: Record<string, string>,
  initialSort?: Record<string, string>
): CommonFilterShape {
  let filters: Record<string, string> = {};

  if (initialSort) {
    getKeys(initialSort).forEach((key) => {
      filters[key] = `${key}=${initialSort[key]}`;
    });
  }

  const getFilter = (search?: string) => {
    for (let key of getKeys(sortingKeys)) {
      const sortingName = sortingKeys[key];
      const { params, hasParams } = getCustomParamFromUrl(sortingName, search ?? window.location.search);

      if (hasParams) {
        if (initialSort) {
          getKeys(initialSort).forEach((key) => {
            delete filters[key];
          });
        }
        filters[sortingName] = `${sortingName}=${params[0]}`;
      }
    }

    return filters;
  };

  return { filters, getFilter };
}

export function useGetPaginationFromUrl(defaultPage?: number): CommonFilterShape {
  const { getParam, addParamToUrl, getUrl, updateHistory } = useUrlResolver();
  let filters: Record<string, string> = {};
  const url = getUrl();
  const page = defaultPage ?? START_PAGE;
  const hasSamePage = Boolean(getParam(PAGE_PARAM_NAME));
  const hasSameLimit = Boolean(getParam(LIMIT_PARAM_NAME));

  if (!hasSameLimit) {
    filters[LIMIT_PARAM_NAME] = `${LIMIT_PARAM_NAME}=${DEFAULT_ITEMS_PER_PAGE}`;
  }

  if (!hasSamePage) {
    filters[PAGE_PARAM_NAME] = `${PAGE_PARAM_NAME}=${page}`;
  }
  const getFilter = (search?: string) => {
    const { page, limit, hasParams } = getPaginationParamsFromUrl(search ?? window.location.search);

    if (limit) {
      filters.limit = `limit=${limit}`;
    }
    if (page || defaultPage) {
      filters.page = `page=${page ?? defaultPage}`;
    }
    if (hasParams) {
      const outOfArea = limit !== null ? !ITEMS_PER_PAGE_LIST.includes(Number(limit)) : false;

      if (outOfArea) {
        addParamToUrl({ name: LIMIT_PARAM_NAME, value: `${DEFAULT_ITEMS_PER_PAGE}`, url });
        filters.limit = `limit=${DEFAULT_ITEMS_PER_PAGE}`;

        addParamToUrl({ name: PAGE_PARAM_NAME, value: START_PAGE, url });
        filters.page = `page=${START_PAGE}`;

        updateHistory({ url });
      }
    }

    return filters;
  };

  return { filters, getFilter };
}

interface GetInitialParamsFromDateParams {
  filterKeys: Record<string, string>;
  setDefaultDate?: boolean;
}

export function useGetInitialParamsFromDate({
  filterKeys,
  setDefaultDate = true,
}: GetInitialParamsFromDateParams): CommonFilterShape {
  let initialDate: Record<string, string> = {};

  const getFilter = (search?: string) => {
    if (filterKeys.from && filterKeys.to) {
      const { from, to } = getDateParamsFromUrl(filterKeys.from, filterKeys.to, search ?? window.location.search);

      if (from && to) {
        initialDate = {
          [filterKeys.from]: `${filterKeys.from}=${dateToString(from)}`,
          [filterKeys.to]: `${filterKeys.to}=${dateToString(to)}`,
        };
      } else if (setDefaultDate) {
        initialDate = {
          [filterKeys.from]: `${filterKeys.from}=${dateToString(getDefaultStartDate())}`,
          [filterKeys.to]: `${filterKeys.to}=${dateToString(getDefaultEndDate())}`,
        };
      }
    }

    return initialDate;
  };

  return {
    filters: initialDate,
    getFilter,
  };
}

interface UseGetInitialFiltersFromUrlParams {
  filterKeys: Record<string, string>;
  keysWithFromTo?: string[];
  defaultFilters?: Record<string, string>;
}

export function useGetInitialFiltersFromUrl({
  filterKeys,
  keysWithFromTo,
  defaultFilters,
}: UseGetInitialFiltersFromUrlParams): CommonFilterShape {
  let initialFilters: Record<string, string> = {};

  if (defaultFilters) {
    getKeys(defaultFilters).forEach((key) => {
      if (key === 'from' || key === 'to' || key === 'search') {
        return;
      }
      if (initialFilters[key]) {
        return;
      }
      initialFilters[key] = `${key}=${defaultFilters[key]}`;
    });
  }
  const getFilter = (search?: string) => {
    getKeys(filterKeys).forEach((key) => {
      if (key === 'from' || key === 'to' || key === 'search') {
        return;
      }
      const filterName = filterKeys[key];

      if (keysWithFromTo?.includes(filterName)) {
        const filtersFromTo = getFromToFiltersFromUrl(filterName, search ?? window.location.search);

        initialFilters = {
          ...initialFilters,
          ...filtersFromTo,
        };

        return;
      }

      const { params, hasParams } = getCustomParamFromUrl(filterName, search ?? window.location.search);

      if (hasParams) {
        initialFilters[filterName] = buildArrayQueryString(filterName, params);
      }
    });

    return initialFilters;
  };

  return {
    filters: initialFilters,
    getFilter,
  };
}

export function useGetInitialSearch(): CommonFilterShape {
  let filters: Record<string, string> = {};

  const getFilter = (search?: string) => {
    const searchFilter = getCustomParamFromUrl(SEARCH_PARAM_NAME, search ?? window.location.search).params[0] || '';

    if (searchFilter) {
      filters[SEARCH_PARAM_NAME] = `search=${searchFilter}`;
    }

    return filters;
  };

  return {
    filters,
    getFilter,
  };
}

const NUMBER_OF_DEFAULT_PARAMS = 2;

export const useIsEnabled = (filters: string) => {
  const url = new URL(filters, window.location.origin);

  const hasPage = url.searchParams.get(PAGE_PARAM_NAME);
  const hasLimit = url.searchParams.get(LIMIT_PARAM_NAME);

  if (hasPage && hasLimit && url.searchParams.size === NUMBER_OF_DEFAULT_PARAMS) {
    return false;
  }

  return true;
};
